import { useCallback, useEffect, useState, useRef, type default as React } from 'react';

import { Box } from '@mui/material';
import { useAuthContext } from 'AuthProvider';
import { MasterSearchFrom, FileDownloadData, SearchBox } from 'components/commonparts'
import { DataList, SearchCondition } from 'components/parts/MasterTransport';
import { useCenter } from 'hooks/useCenter';
import { useCustomer} from 'hooks/useCustomer';
import { useCustomerCenter } from 'hooks/useCustomerCenter';
import { useGridSearch } from 'hooks/useGridSearch';
import { useTransportRelation } from 'hooks/useTransitionRelation';
import { useSearchParamStore } from 'store/searchParamStore';
import { getLocalSearchParam } from 'utils/getLocalSearchParam';

import type { GetMstTransportRelationTransportRelationGetRequest, MstTransportRelation, ResponseResult } from 'api-client';

function MasterTransport() {
  const { loginUserInfo } = useAuthContext()
  const trCompanyId = loginUserInfo?.companyId;
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

 const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)
 const localOrgCenterId = localSearchParam?.transport.orgCenterId || undefined
 const localOrgCustomerId = localSearchParam?.transport.orgCustomerId || undefined

  // 自社のcompanyIdのみ検索条件をローカルストレージから復元
  const initSearchParam: GetMstTransportRelationTransportRelationGetRequest = {
    companyId: trCompanyId ?? loginUserInfo!.companyId,
    orgCenterId: localOrgCenterId,
    orgCustomerId: localOrgCustomerId,
  }

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [downloadGridRows, setDownloadGridRows] = useState<MstTransportRelation[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([]);

  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [searchParam, setSearchParam] = useState<GetMstTransportRelationTransportRelationGetRequest>(initSearchParam)
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)

  const { result, transportRelationSearch, transportRelationSearchIsLoading, fetchTransportRelation, transportRelationSearchResult } = useTransportRelation()
  const { centerSearch, fetchCenterSearch } = useCenter()
  const { customers, fetchCustomer } = useCustomer()
  const { customerCenters, fetchCustomerCenter } = useCustomerCenter()
  const isFirstRender = useRef(true);
  const [uploadData, setUploadData] = useState<any[]>([])
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  
  // Search functionality using custom hook
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })

  useEffect(() => {
    setResultMessage(transportRelationSearchResult)
  }, [transportRelationSearchResult])
  useEffect(() => {
    setResultMessage(result)
  }, [result])
  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage])

  const doSearch = useCallback(async (requestParam: GetMstTransportRelationTransportRelationGetRequest) => {
    if (hasGridEdited) {
      setDiscardChangeDialogOpen(true)
      return
    }
    try {
      fetchTransportRelation({
          enabled: false,
          ...requestParam,
        });
        // fetchSupplier({
        //   companyId: requestParam.companyId, 
        // });
        if (requestParam.companyId === loginUserInfo!.companyId && loginUserInfo?.tenantId && loginUserInfo?.companyId) {
          setSearchParamState<GetMstTransportRelationTransportRelationGetRequest>(
            loginUserInfo?.tenantId,
            loginUserInfo?.companyId,
            'Transport',
            {
              ...requestParam,
            }
          );
        }
  
        setSearchParam(requestParam);
        setHasGridEdited(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error);
      } 
  }, [transportRelationSearch]);

  const handleRegister = () => {
    setUpdateTrigger((prev) => !prev);
  }

  const handleUploadData = async (headers: any[], data: any[]) => {
    // エクセルファイルからデータをインポートし、ヘッダーとデータ行に分けて渡される
    const mappedData = data.map((row) => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
    setUploadData(mappedData); 
  };

  const onReload = () => {
    // 検索処理の実行を行うトリガー
    setSearchTrigger((prev) => !prev);
  }

  useEffect(() => {
    const firstTimeRender = async () => { 
      if (isFirstRender.current) {
        isFirstRender.current=false
        const itemsPromise = fetchTransportRelation({
          enabled: false,
          ...searchParam,
        });
        const CenterPromise = fetchCenterSearch({
          enabled: false,
          ...searchParam,
        }, true);
        const customerCenterPromise = fetchCustomerCenter({
          enabled: false,
          ...searchParam,
        }, true);
        const customerPromise = fetchCustomer({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        await Promise.all([itemsPromise, CenterPromise, customerCenterPromise, customerPromise]);
      }
    }
    firstTimeRender()
  }, [])

  const generateDownloadData = (): MstTransportRelation[] => {
    if (!transportRelationSearch?.list || transportRelationSearch.list.length === 0) {
      return [{} as MstTransportRelation]
    }
    const downloadData: MstTransportRelation[] = [...transportRelationSearch.list]
    return downloadData
  }
  
  const handleFileDownload = () => {
    setDownloadGridRows(generateDownloadData())
  }

  return (
    <Box>
      <MasterSearchFrom
          result={resultMessage}
          onDoUpdate={handleRegister}
          onReload={onReload}
          onImportData={handleUploadData}
          onFileDownLoad={handleFileDownload}
          discardChangeDialogOpen={discardChangeDialogOpen}
          setDownloadMode={setDownloadMode}
          hasDataEdited={hasGridEdited}
          importRequiredFields={["org_center_id", "org_customer_id", "org_customer_center_id"]}
          headerJp={["発送元センターID", "得意先企業ID", "発送先センターID"]}
          lastUpdateDatetime={transportRelationSearch?.lastUpdateDatetime}
        >
          <SearchCondition
            hasDataEdited={hasGridEdited}
            searchParam={initSearchParam}
            onSearch={doSearch}
            setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
            searchTrigger={searchTrigger}
          />
        </MasterSearchFrom>
      <FileDownloadData fileName="輸送" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />
      <SearchBox
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onToggle={gridSearch.searchBoxProps.onToggle}
        onShow={gridSearch.searchBoxProps.onShow}
        onHide={gridSearch.searchBoxProps.onHide}
      />
      <DataList
          list={transportRelationSearch}
          center={centerSearch.list}
          customer={customers.list}
          customerCenter={customerCenters.list}
          onReload={onReload}
          isLoading={transportRelationSearchIsLoading}
          searchParam={searchParam}
          onUploadData={uploadData}
          updateTrigger={updateTrigger}
          setHasGridEdited={setHasGridEdited}
          setResultMessage={setGridMessage}
          setDownloadColumnDefs={setDownloadColDef}
          gridRef={gridSearch.gridRef}
          findSearchValue={gridSearch.gridSearchProps.findSearchValue}
          onFindChanged={gridSearch.gridSearchProps.onFindChanged}
          isSearchVisible={gridSearch.searchBoxProps.isVisible}
        />

    </Box>
          
  )
}

export default MasterTransport;
